import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, ChevronLeft, PackageCheck, Truck } from "lucide-react";
import { api, eur, inStock, isFreeShipping } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { ProductGrid } from "@/components/ProductCard";
import { Gallery } from "@/components/Gallery";
import { OptionPicker, initialSelection, selectedValues } from "@/components/OptionPicker";
import { CloudFreeNotice, KlarnaMessage } from "@/components/ProductNotices";

export default function Product() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [sel, setSel] = useState({});
  const [qty, setQty] = useState(1);
  const { add } = useCart();

  useEffect(() => {
    setData(null);
    api.get(`/products/${slug}`).then((r) => {
      setData(r.data);
      setSel(initialSelection(r.data.product.options));
      setQty(r.data.product.min_qty || 1);
    }).catch(() => setData(false));
  }, [slug]);

  const p = data?.product;
  const extras = useMemo(() => (p ? selectedValues(p.options, sel) : []), [p, sel]);
  if (data === false) return <div className="py-32 text-center text-slate-500" data-testid="product-not-found">Prodotto non trovato. <Link to="/catalogo" className="text-[#4D94FF]">Torna al catalogo</Link></div>;
  if (!p) return <div className="mx-auto h-[70vh] max-w-7xl animate-pulse px-6 py-10"><div className="h-full rounded-3xl bg-slate-100" /></div>;

  const unit = p.price + extras.reduce((s, x) => s + x.markup, 0);
  const available = inStock(p);
  const maxQty = p.inventory_tracked ? p.quantity : p.max_qty || 99;

  const addToCart = () => {
    const missing = p.options.filter((o) => o.kind !== "checkbox" && o.required && !sel[o.name]);
    if (missing.length) return toast.error(`Seleziona: ${missing.map((o) => o.name).join(", ")}`);
    add({ key: `${p.id}|${extras.map((x) => `${x.name}=${x.value}`).join("|")}`, product_id: p.id, slug: p.slug, name: p.name,
      image: p.images?.[0]?.url || "", unit_price: unit, options: extras, qty, free_shipping: isFreeShipping(p) });
    toast.success("Aggiunto al carrello");
  };

  return (
    <div className="mx-auto max-w-7xl px-4 pt-8 sm:px-6" data-testid="product-page">
      <Link to={p.category_path ? "/catalogo" : "/catalogo"} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#0066FF]"><ChevronLeft className="h-4 w-4" /> Catalogo</Link>
      <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
        <Gallery images={p.images} />
        <div className="min-w-0 lg:py-4">
          {p.ribbon && <span className="inline-block rounded-full bg-[#0066FF]/10 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-[#0066FF]">{p.ribbon}</span>}
          <h1 className="mt-4 font-display text-3xl font-bold leading-tight tracking-[-0.03em] text-[#0A0B0E] sm:text-4xl" data-testid="product-title">{p.name}</h1>
          {p.subtitle && <p className="mt-3 text-sm text-slate-500">{p.subtitle}</p>}
          <div className="mt-6 flex items-baseline gap-3">
            <span className="font-display text-4xl font-bold text-[#0A0B0E]" data-testid="product-price">{eur(unit)}</span>
            {p.compare_price > p.price && <span className="text-slate-400 line-through">{eur(p.compare_price + (unit - p.price))}</span>}
            {p.discount_pct > 0 && <span className="rounded-full bg-[#0066FF] px-2 py-0.5 text-xs font-bold text-white">-{p.discount_pct}%</span>}
          </div>
          {p.sku && <p className="mt-2 font-mono text-[11px] text-slate-400">COD. {p.sku}</p>}
          <KlarnaMessage price={unit} />
          <CloudFreeNotice p={p} />
          <div className="mt-8 space-y-6">{p.options.map((o) => <OptionPicker key={o.name} option={o} sel={sel} setSel={setSel} />)}</div>
          <div className="mt-8 flex gap-3">
            <div className="flex h-14 items-center rounded-full border border-slate-300 text-slate-900">
              <button className="h-full px-4 text-lg text-slate-500" onClick={() => setQty(Math.max(p.min_qty || 1, qty - 1))} data-testid="product-qty-decrease" aria-label="Diminuisci">−</button>
              <span className="w-8 text-center font-mono" data-testid="product-qty">{qty}</span>
              <button className="h-full px-4 text-lg text-slate-500" onClick={() => setQty(Math.min(maxQty, qty + 1))} data-testid="product-qty-increase" aria-label="Aumenta">+</button>
            </div>
            <button disabled={!available} onClick={addToCart} data-testid="product-add-to-cart"
              className="h-14 flex-1 rounded-full bg-[#0066FF] font-semibold text-white shadow-[0_12px_30px_-10px_rgba(0,102,255,0.7)] transition-[background-color,transform] hover:-translate-y-0.5 hover:bg-[#1a75ff] disabled:cursor-not-allowed disabled:bg-slate-300 disabled:shadow-none">
              {available ? "Aggiungi al carrello" : "Esaurito"}
            </button>
          </div>
          <ul className="mt-8 space-y-3 border-t border-slate-200 pt-6 text-sm text-slate-600">
            <li className="flex items-center gap-3" data-testid="product-availability"><PackageCheck className="h-4 w-4 text-[#0066FF]" />
              {available ? (p.inventory_tracked ? `Disponibile · ${p.quantity} pezzi` : "Disponibile") : "Al momento non disponibile"}</li>
            <li className="flex items-center gap-3"><Truck className="h-4 w-4 text-[#0066FF]" />{p.shipping_required === false ? "Consegna digitale" : isFreeShipping(p) ? "Spedizione gratuita" : "Spedizione calcolata nel carrello"}</li>
            <li className="flex items-center gap-3"><Check className="h-4 w-4 text-[#0066FF]" /><Link to="/pagina/garanzia" className="hover:text-[#0066FF]">Condizioni di garanzia</Link></li>
          </ul>
        </div>
      </div>
      {p.description && (
        <section className="mt-16">
          <h2 className="font-display text-2xl font-bold text-[#0A0B0E]">Descrizione</h2>
          <div className="lt-desc mt-5 rounded-3xl border border-slate-200 bg-[#F5F7FA] p-6 sm:p-10" data-testid="product-description" dangerouslySetInnerHTML={{ __html: p.description }} />
        </section>
      )}
      {data.related.length > 0 && (
        <section className="mt-20">
          <h2 className="mb-6 font-display text-2xl font-bold text-[#0A0B0E]">Potrebbe interessarti</h2>
          <ProductGrid items={data.related} testid="related-grid" />
        </section>
      )}
    </div>
  );
}
